import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import LocalStorageService from '../services/localStorageService'
import { getProfile, updateProfile } from '../services/api/userApi'
import { queryKeys } from '../services/queryKeys'
import { User } from '../models/User'

export interface UserContextType {
  user: User | null
  updateUserInfo: (newUserInfo: Partial<User>) => void
  removeUserInfo: () => void
}

export const UserContext = createContext<UserContextType | undefined>(undefined)

interface UserProviderProps {
  children: ReactNode
}

const getStoredUser = (): User | null =>
  LocalStorageService.getItem<User>('user')

function UserProvider({ children }: UserProviderProps) {
  const [storedUser, setStoredUser] = useState<User | null>(getStoredUser)
  const queryClient = useQueryClient()

  const profileQuery = useQuery({
    queryKey: queryKeys.profile(storedUser?.userId),
    queryFn: getProfile,
    enabled: Boolean(storedUser?.userId),
    staleTime: 5 * 60_000,
  })

  useEffect(() => {
    if (!profileQuery.data) return

    setStoredUser((currentUser) => {
      const mergedUser = {
        ...(currentUser ?? {}),
        ...profileQuery.data,
        isProfileUpdated: true,
        displayName:
          `${profileQuery.data.firstName ?? ''} ${profileQuery.data.lastName ?? ''}`.trim() ||
          profileQuery.data.displayName ||
          currentUser?.displayName ||
          '',
      }
      const nextUser = {
        ...(currentUser ?? {}),
        ...mergedUser,
      }
      LocalStorageService.setItem('user', nextUser)
      return nextUser
    })

    if (!profileQuery.data.timezone) {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      if (timezone) {
        void updateProfile({ timezone }).catch(() => {})
      }
    }
  }, [profileQuery.data])

  const updateUserInfo = useCallback((newUserInfo: Partial<User>) => {
    setStoredUser((previousUser) => {
      const updatedInfo = { ...(previousUser ?? {}), ...newUserInfo }
      LocalStorageService.setItem('user', updatedInfo)
      return updatedInfo
    })
    // Invalidate the profile query so getProfile fetches the full profile
    // (including profilePictureUrl) from the server
    const userId = newUserInfo.userId
    if (userId) {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.profile(userId),
      })
    }
  }, [queryClient])

  const removeUserInfo = useCallback(() => {
    LocalStorageService.removeItem('user')
    setStoredUser(null)
    queryClient.clear()
  }, [queryClient])

  const value = useMemo(
    () => ({
      user: storedUser,
      updateUserInfo,
      removeUserInfo,
    }),
    [removeUserInfo, storedUser, updateUserInfo]
  )

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}

export const useUserContext = (): UserContextType => {
  const context = useContext(UserContext)

  if (!context) {
    throw new Error('useUserContext must be used within UserProvider')
  }

  return context
}

export default UserProvider
